import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {MaintenanceManagerService} from "../../../services/maintenance-manager.service";
import {Maintenance, MaintenanceStatus} from "../../../models/maintenance";
import {Assistance, AssistanceType} from "../../../models/assistance";
import {TimelineElementStatus, TimelineGroup} from "../../../components/timeline/timeline.component";
import {CardAction, ScottyCardComponent} from "../../../components/scotty-card/scotty-card.component";
import {EventType} from "../../../models/calendar-event";
import {Paginator} from "../../../models/paginator";
import {Resource} from "../../../models/resource";
import {AuthoringManagerService} from "../../../services/authoring-manager.service";
import {Step} from "../../../models/step";
import {UiManagerService} from "../../../services/ui-manager.service";
import {ResourceManager} from "../../../services/resource-manager";

@Component({
  selector: 'app-maintenance-single-chat-page',
  templateUrl: './maintenance-single-chat-page.component.html',
  styleUrls: ['./maintenance-single-chat-page.component.scss']
})
export class MaintenanceSingleChatPageComponent implements OnInit {

  updatingProcedure = false;

  maintenance: Maintenance;
  isVideoPlaybackShown: boolean;

  tags: string[] = [];

  /* Maintenance progress */
  @ViewChild('procedureStatusCard', { static: false })
  procedureStatusCard: ScottyCardComponent;
  progress: TimelineGroup[];

  constructor(
    private route: ActivatedRoute,
    private maintenanceManager: MaintenanceManagerService,
    private authoringManagerService: AuthoringManagerService,
    private uiManagerService: UiManagerService,
    private resourceManager: ResourceManager,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit(): Promise<void> {

    this.route.paramMap.subscribe(async params => {

      // Get maintenance
      this.maintenance = await this.maintenanceManager.getMaintenance(params.get('maintenance-id'));

      // Get maintenance progress
      await this.getProcedureProgress();

      this.tags = await this.maintenanceManager.getTagsFromMaintenance(this.maintenance).catch(_ => {}) || [];

    });

  }

  async getProcedureProgress() {
    if (this.maintenance && this.maintenance.procedure) {
      this.progress = null;
      this.updateMaintenanceStatusUI();
      this.progress = (await this.maintenanceManager.getMaintenanceProgress(this.maintenance));
      this.updateMaintenanceStatusUI();
    }
  }

  updateMaintenanceStatusUI() {
    this.cdr.detectChanges();
    this.procedureStatusCard?.reloadCardActions();
  }

  getAssistanceFromMaintenance(): Assistance {
    return {
      id: this.maintenance.event.id,
      maintenance: this.maintenance,
      event: this.maintenance.event,
      status: this.maintenance.status,
      equipment: this.maintenance.equipment,
      fieldWorker: this.maintenance.fieldWorker
    } as Assistance;
  }

  playbackRecording() {
    this.isVideoPlaybackShown = true;
  }

  stopPlayback() {
    this.isVideoPlaybackShown = false;
  }

  getProcedureCardActions(): CardAction[] {

    const actions = [];

    if (this.progress) {

      if (this.isProcedureCompleted()) {

        if (this.canBindVideoToProcedure()) {
          actions.push({
            icon: 'pending_actions',
            disabled: this.updatingProcedure,
            name: 'Bind video and seeks to procedure',
            callback: () => {
              this.bindVideoAndSeeksToProcedure();
            }
          });
        }

      } else {
        actions.push({
          icon: 'refresh',
          name: 'Refresh',
          callback: () => {
            this.getProcedureProgress();
          }
        });
      }

    }

    return actions;
  }

  isProcedureCompleted() {
    return this.progress && this.progress.every(timelineGroup => timelineGroup.elements.every(element => element.status != TimelineElementStatus.NONE));
  }

  canBindVideoToProcedure() {
    return this.maintenance.recordingUrl && this.maintenance.procedure;
  }

  bindVideoAndSeeksToProcedure() {

    this.uiManagerService.showConfirmDialog('Update procedure', 'Do you want to update the procedure with current informations?', async () => {

      this.updatingProcedure = true;
      this.updateMaintenanceStatusUI();

      // Bind recording url to procedure
      // This can lead to bug in future (now maintenances in self authoring mode are admitted with a sigle resource bound to maintenance, in future maybe not)
      const recordingResource = (await this.maintenanceManager.getResources(this.maintenance, new Paginator<Resource>({ignorePagination: true})))[0];
      const resourceMetadata = await this.resourceManager.getResourceMetadata(recordingResource, new Paginator({ ignorePagination: true }));
      const videoBindPromise = this.authoringManagerService.bindResourceToProcedure(this.maintenance.procedure, recordingResource);

      // Bind steps timestamp to steps
      let seekStart = 0;
      const data = this.progress.map(timelineGroup => {
        return timelineGroup.elements.map((element, elemIndex) => {

          const seekEnd = (element.timestamp.getTime() - resourceMetadata.recStart) / 1000;

          const step = new Step();
          step.id = element.id;
          step.taskId = timelineGroup.id;
          step.name = element.title;
          step.description = element.description;
          step.sequence = elemIndex;
          step.seekStart = seekStart;
          step.seekEnd = seekEnd > 0 ? seekEnd : 0;
          seekStart = step.seekEnd;
          return step;
        });
      });
      const steps = [].concat(...data);
      const updateStepsPromises = steps.map(step => this.authoringManagerService.updateStep(step.id, step));

      // Set procedure with video
      this.maintenance.procedure.hasVideo = true;
      const updateProcedurePromise = await this.authoringManagerService.updateProcedure(this.maintenance.procedure.id, this.maintenance.procedure);

      await Promise.all([
        videoBindPromise,
        updateProcedurePromise,
        ...updateStepsPromises
      ]);

      this.uiManagerService.showInfoDialog('Procedure updated', 'The corresponding procedure is successfully updated.');

      this.updatingProcedure = false;
      this.updateMaintenanceStatusUI();

    });

  }

  get AssistanceType() { return AssistanceType }

  get MaintenanceStatus() { return MaintenanceStatus }

  get EventType() { return EventType }

}
